<template>
  <v-app id="inspire">
    <div class="wrap-service-plan-show invoice-view-wrap">
      <!-- Header -->
      <div class="sr-pl-show-top-header">
        <v-layout>
          <div>
            <div class="caption mb-2">
              <span 
                  @click="$router.push('/settings')"
                  class="pointer primary--text text-uppercase"
              >Settings</span>
              <span class="px-2 grey--text">/</span>
              <span
                  @click="$router.push('/settings/royalty_payments')"
                  class="pointer primary--text text-uppercase"
              >Royalty Payments</span>
              <template v-if="!no_data.includes(summary_items_number)">
                  <span class="px-2 grey--text">/</span>
                  <span
                      class="grey--text text-uppercase"
                  >{{ summary_items_number }}</span>
              </template>
            </div>
            <p class="header-p header-inv-num">
              Invoice #: <span class="headline">{{ summary_items_number }}</span>
            </p>
          </div>
          <v-spacer></v-spacer>
          <v-btn class="header-btn-back mt-0 mr-0 mb-3" @click="$router.go(-1)">
            <v-icon class="header-btn-back-icon">navigate_before</v-icon>
            Back
          </v-btn>
        </v-layout>
        <v-layout>
          <v-flex xs6>
            <v-layout>
              <span>{{ removeUnderScore(summary_items_type) }}</span>
            </v-layout>
            <div
              class="mt-1"
              v-if="summary.status != 'draft' && parseFloat(summary.total) > 0"
            >
            </div>
          </v-flex>
        </v-layout>
      </div>
      <!-- Summary table -->
      <v-card>
        <v-data-table
          :headers="summary_headers"
          :items="summary_items"
          hide-default-footer
          class="elevation-0 sr-pl-sh-table"
          colwidth-1="30px"
          :loading="loading"
          loader-height="2"
          mobile-breakpoint="100"
        >
          <template v-slot:item="{item}">
            <tr>
              <td>Location</td>
              <td>{{ item.organization_name }}</td>
              <td>Status</td>
              <td :class="`invoices-type-${item.status}`">
                <div class="invoices-status-text">
                  <span>{{ titleCase(item.status) }}</span>
                </div>
              </td>
            </tr>
            <tr>
              <td>Address</td>
              <td>{{ item.organization_address }}</td>
              <td>Email</td>
              <td>{{ item.organization_email }}</td>
            </tr>
            <tr>
              <td>Start of Billing Period</td>
              <td>{{ onlyDate(item.period_start) }}</td>
              <td>End of Billing Period</td>
              <td>{{ onlyDate(item.period_end) }}</td>
            </tr>
            <tr>
              <td>Invoice #</td>
              <td>{{ item.invoice_number }}</td>
              <td>Net Terms</td>
              <td>
                <span v-if="!no_data.includes(item.net_term)"
                  >NET {{ item.net_term }}</span
                >
                <em v-else class="grey--text">No data</em>
              </td>
            </tr>
            <tr>
              <td>Date Created</td>
              <td>{{ onlyDate(item.created_at) }}</td>
              <td></td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <v-card v-if="description_items.length !== 0">
        <!-- Description  table -->
        <v-data-table
          :headers="description_headers"
          :items="description_items"
          hide-default-footer
          disable-pagination
          class="elevation-0 sr-pl-sh-table sr-pl-sh-table-pricing"
          colwidth-1="30px"
          mobile-breakpoint="100"
        >
          <template v-slot:item="{item}">
            <tr>
              <td>{{ item.description }}</td>
              <td>{{ item.quantity }}</td>
              <td>
                ${{ withCommas(parseFloat(item.unit_amount).toFixed(2)) }}
              </td>
              <td></td>
              <td>
                ${{ withCommas(parseFloat(item.amount).toFixed(2)) }}
              </td>
            </tr>
          </template>
        </v-data-table>
        <v-divider></v-divider>
        <!-- Invoice total section -->
        <div class="inv-desc-table-style inv-desc-total">
          <div class="inv-desc-amount-titles">
            <p>Subtotal</p>
            <p>Tax</p>
            <p class="inv-total-text">Invoice Total</p>
            <template v-if="summary.status !== 'draft'">
              <p>Amount Received</p>
              <p class="inv-total-text">Remaining Balance</p>
            </template>
          </div>
          <div class="inv-desc-amount-values">
            <p>${{ withCommas(parseFloat(summary.subtotal).toFixed(2)) }}</p>
            <p>${{ withCommas(parseFloat(summary.tax).toFixed(2)) }}</p>
            <p class="inv-total-text">
              ${{ withCommas(parseFloat(summary.total).toFixed(2)) }}
            </p>
            <template v-if="summary.status !== 'draft'">
              <p>${{ withCommas(parseFloat(summary.paid).toFixed(2)) }}</p>
              <p class="inv-total-text">
                ${{ withCommas(parseFloat(summary.remaining).toFixed(2)) }}
              </p>
            </template>
          </div>
        </div>
        <div v-if="summary.client_note" class="memo inv-desc-table-style">
          <strong>Note to Client:</strong> {{ summary.client_note }}
        </div>
        <div v-if="summary.note" class="memo inv-desc-table-style">
          <strong>Internal Memo:</strong> {{ summary.note }}
        </div>
      </v-card>
    </div>
  </v-app>
</template>

<script>
import ApiService from "@/common/api.service"
import { validationMixin } from "vuelidate"
import { required, decimal, helpers, email } from "vuelidate/lib/validators"
import { valMessages, getValMessage } from "@/common/error-messages"
import { mapGetters, mapActions } from 'vuex'

const validations = {
  receiveCheckData: {
    check_amount: {
      required,
      decimal,
      positive: v => !helpers.req(v) || v > 0,
      currency: v => !helpers.req(v) || /^([1-9][0-9]*)+(\.[0-9]{1,2})?$/.test(v) || /^0+(\.[0-9]{1,2})?$/.test(v)
    },
    check_date: { required },
    check_reference_number: { required }
  }
}

export default {
  name: 'InvoiceIndividualView',
  mixins: [validationMixin],
  validations,
  data() {
    return {
      no_data: [null, "", undefined, "null"],
      invoiceNumber: "",
      invoiceSubType: "",
      loading: false,
      loadingEmail: false,
      loadingPrint: false,
      disable_radio_btn: false,
      pay_method: "",
      initial_pay_method: null,
      billing: "1",
      summary_headers: [
        {
          text: "Summary",
          align: "left",
          sortable: false,
          value: "Summary",
          width: "20%"
        },
        {
          sortable: false
        },
        {
          sortable: false,
          width: "20%"
        },
        {
          sortable: false
        }
      ],
      description_headers: [
        {
          text: "Description",
          align: "left",
          sortable: false,
          value: "Description"
        },
        {
          text: "Quantity",
          sortable: false,
          value: "Quantity"
        },
        {
          text: "Rate",
          sortable: false,
          value: "Rate",
          width: "20%"
        },
        {
          sortable: false
        },
        {
          text: "Amount",
          sortable: false,
          value: "Amount",
          width: "200px"
        }
      ],
      summary_items: [],
      description_items: [],
      summary_header_items: [],
      summary_items_type: "",
      summary_items_number: "",
      summary: "",
      summary_status: "",
      summary_id: "",
      categories: [
        { text: "Postpaid Fixed", value: "postpaid" },
        { text: "Prepaid Fixed", value: "prepaid" },
        { text: "Postpaid Per Cleanup", value: "cleanup" },
        { text: "Variable", value: "variable" }
      ],
      billingIntervals: [
        { text: "Annually", value: "annually" },
        { text: "Semi-Annually", value: "semi-annually" },
        { text: "Every Four Months", value: "every_four_months" },
        { text: "Quarterly", value: "quarterly" },
        { text: "Every Two Months", value: "every_two_months" },
        { text: "Monthly", value: "monthly" },
        { text: "Every 4 Weeks", value: "4_weeks" },
        { text: "Bi-weekly", value: "bi-weekly" },
        { text: "Weekly", value: "weekly" },
        { text: "Daily", value: "daily" }
      ],

      loadingSave: false,
      formValidation: true,
      serverValidation: true,
      serverValidationMessage: "Oops something went wrong...",
      server_errors: {},
      menu3: false,
      receiveCheckData: {
        check_amount: null,
        check_reference_number: null,
        check_date: null
      },
      required: v => !!v || "This field is required",
      currency: v =>
        ((/^([1-9][0-9]*)+(\.[0-9]{1,2})?$/.test(v) ||
          /^0\.+([0-9]{1,2})?$/.test(v) ||
          v === undefined ||
          String(v) === "") &&
          Number(v) > 0) ||
        "Enter valid currency format",
      invoice: {},
      surce_info: null,

      loadingContacts: false,
      emailContacts: []
    };
  },
  beforeMount() {
    this.getDataFromApi();
  },
  watch: {
    billing: {
      handler() {
        this.$v.receiveCheckData.$reset()
        if (this.initial_pay_method === "credit_card") {
          this.billing === "0"
            ? (this.pay_method = "check")
            : (this.pay_method = "credit_card");
        }
      }
    }
  },
  methods: {
    ...mapActions(['vEmit']),
    getDataFromApi(id = this.$route.params.id) {
      this.loading = true;
      this.plan_id = id;
      ApiService.get("/franchise/invoice/" + id)
        .then(response => {
          let data = response.data;
          let summary_items = data.invoice;
          let description_items = data.invoice_items;
          // let billing = data.client_have_stripe.toString();
          let billing = "1"
          //Create amount property
          for (let i in description_items) {
            description_items[i].amount = (
              description_items[i].quantity * description_items[i].unit_amount
            ).toFixed(2);
          }
          //If tax is null make it 0.00
          if (summary_items.tax == null) {
            summary_items.tax = "0.00";
          }
          this.initial_pay_method = summary_items.pay_method
          this.pay_method = summary_items.pay_method
          //Summary
          this.summary_items = [summary_items];
          this.summary = summary_items;
          this.summary_items_type = summary_items.type;
          this.summary_items_number = summary_items.invoice_number;
          this.summary_status = summary_items.status;
          this.summary_id = summary_items.id;
          this.getContacts()
          //Descriptions
          this.description_items = description_items;
          this.billing = billing;
          this.loading = false;
          this.surce_info = data.source_info;
        })
        .catch(error => {
          console.log(error);
        });
    },
    updateInvoice() {
      this.resetServerValidation();
      let content = {
        invoice_items: this.description_items,
        pay_method: this.pay_method,
        invoice_id: this.summary.id
      };
      this.loadingSave = true;

      ApiService.put("/invoice_items", content)
        .then(response => {
          this.loadingSave = false;
          this.getDataFromApi();
        })
        .catch(({ response }) => {
          this.loadingSave = false;
          this.serverValidation = false;
          if (response.data.error) {
            this.serverValidationMessage = _.upperFirst(
              response.data.error.replace(/_/g, " ")
            );
          }
        });
    },
    createPayment() {
      this.resetServerValidation()
      if (this.pay_method === "check") { this.$v.receiveCheckData.$touch() }
      if (!this.$v.receiveCheckData.$invalid || this.pay_method !== "check") {
        let body = {
          pay_method: this.pay_method,
          invoice_id: this.summary.id
        };
        if (this.pay_method === "check") {
          body = { ...body, ...this.receiveCheckData };
        }
        this.loadingSave = true;
        ApiService.put("/pay_invoice", body)
          .then(response => {
            (this.receiveCheckData = {
              check_amount: null,
              check_reference_number: null,
              check_date: null
            }),
            this.$v.receiveCheckData.$reset()
            this.loadingSave = false;
            this.getDataFromApi();
            this.vEmit({ 
              name: "notification", 
              data: {
                status: "success",
                text: "Payment has been initiated."
              }
            });
          })
          .catch(({ response }) => {
            this.loadingSave = false;
            this.serverValidation = false;
            if (response.data.error) {
              this.serverValidationMessage = this.titleCase(
                response.data.error
              );
            }
            if (response.data.message) {
              this.serverValidationMessage = response.data.message;
            }
            if (response.data.errors) {
              this.server_errors = response.data.errors;
            }
          });
      }
    },
    getContacts () {
      if(
        !this.no_data.includes(this.summary.commercial_client_id) &&
        !this.no_data.includes(this.summary.commercial_location_id)
      ) {
        const body = {
          organization_id: this.organizationId,
          commercial_client_id: this.summary.commercial_client_id,
          commercial_location_id: this.summary.commercial_location_id
        } 
        this.loadingContacts = true
        ApiService.get('/commercial/contacts', body)
          .then(response => {
            let contacts = response.data.commercial_contacts
            contacts = contacts.filter(i => {
              return !this.no_data.includes(i.email) &&
                    !this.no_data.includes(i.email_invoices) &&
                    Boolean(i.email_invoices)
            })
            this.emailContacts = contacts.map(i => i.email)
            this.loadingContacts = false
          })
          .catch(error => {
            console.log(error)
            this.loadingContacts = false
          });
      } else if (!this.no_data.includes(this.summary.client_id)) {
        this.loadingContacts = true
        ApiService.get('/client/details/additional_contacts', { client_id: this.summary.client_id })
          .then(response => {
            let contacts = response.data.additional_contacts
            contacts = contacts.filter(i => {
              return !this.no_data.includes(i.email) &&
                    !this.no_data.includes(i.invoice) &&
                    Boolean(i.invoice)
            })
            contacts = contacts.map(i => i.email)
            let emailContacts = []
            if (!this.no_data.includes(this.summary.client_email)) {
              emailContacts.push(this.summary.client_email)
            }
            emailContacts = [...emailContacts, ...contacts]
            this.emailContacts = emailContacts
            this.loadingContacts = false
          })
          .catch(error => {
            console.log(error)
            this.loadingContacts = false
          })
      }
    },
    //Back to One Time Invoices table
    backToInvoicesList() {
      if (this.summary.type === "subscription") {
        this.$router.push("/recurring_invoices");
      } else {
        this.$router.push("/one_time_invoices");
      }
    },
    //Remove underscores from response
    removeUnderScore(str) {
      for (let i in str) {
        str = str.replace("_", " ");
      }
      return (str = str.replace(/\w\S*/g, function(txt) {
        return txt.toUpperCase();
      }));
    },
    onlyNumbers($event) {
      let keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) {
        // 46 is dot
        $event.preventDefault();
      }
    },
    titleCase(str) {
      return _.startCase(str.replace(/_/g, " "));
    },
    //Make a firts character capital of string from response
    makeCapitalFirstLetter(str) {
      str = str.toLowerCase();
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    //Go to Individual Invoice Edit
    goToInvoiceEdit(id) {
      this.$router.push(`/invoice/${id}/edit`);
    },
    resetServerValidation() {
      this.serverValidation = true;
      this.serverValidationMessage = "Oops something went wrong...";
    },
    withCommas(i) {
      var parts = i.toString().split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts.join(".");
    },
    pdfInvoice(id, invoice_number) {
      this.loadingPrint = true;
      ApiService.get("/invoice/" + id + "/pdf")
        .then(response => {
          this.loadingPrint = false;
          if (response.data.pdf_invoice) {
            let linkSource = `data:application/pdf;base64,${response.data.pdf_invoice}`;
            let downloadLink = document.createElement("a");
            let fileName = invoice_number + ".pdf";

            downloadLink.href = linkSource;
            downloadLink.download = fileName;
            downloadLink.click();
          } else {
            this.vEmit({ 
              name: "notification", 
              data: {
                status: "error",
                text: "Oops something went wrong..."
              }
            });
          }
        })
        .catch(error => {
          this.loadingPrint = false;
          console.log(error);
          this.vEmit({ 
            name: "notification", 
            data: {
              status: "error",
              text: "Oops something went wrong..."
            }
          });
        });
    },
    emailInvoice(id) {
      this.loadingEmail = true;
      ApiService.get("/invoice/" + id + "/email")
        .then(response => {
          this.loadingEmail = false;
          this.vEmit({ 
            name: "notification", 
            data: {
              status: "success",
              text: "Invoice was sent by email to client."
            }
          });
        })
        .catch(error => {
          this.loadingEmail = false;
          console.log(error);
          this.vEmit({ 
            name: "notification", 
            data: {
              status: "error",
              text: "Oops something went wrong..."
            }
          });
        });
    },
    onlyDate(date) {
        return moment(date).format('YYYY-MM-DD');
    },
    resetServerValidation() {
      this.serverValidation = true
      this.serverValidationMessage = "Oops something went wrong..."
      this.server_errors = {}
    },
    errors: getValMessage(valMessages)(validations)
  },
  computed: {
    ...mapGetters(["organizationId"])
  }
};
</script>

<style>
/*Header*/
.wrap-service-plan-show .name-plan {
  font-size: x-large;
  font-weight: 500;
}

.wrap-service-plan-show .header-p {
  font-size: small;
}

.wrap-service-plan-show .header-p:first-of-type {
  font-size: medium;
  margin-bottom: 0px;
}

.wrap-service-plan-show .header-inv-num {
  display: inline-block;
}

.wrap-service-plan-show .header-btn-back {
  float: right;
  box-shadow: none !important;
  background-color: #f8f9fa !important;
  text-transform: none;
  font-weight: 400;
}
.wrap-service-plan-show .header-btn-back:focus {
  outline: none;
}
.wrap-service-plan-show .header-btn-back-icon {
  margin: 2px -5px 0px 0px;
}

/*Tables*/
.wrap-service-plan-show .sr-pl-sh-table {
  margin: 40px 0px 0px 0px;
}
.wrap-service-plan-show .sr-pl-sh-table:first-child {
  margin: 20px 0px 0px;
}
/* .sr-pl-sh-table thead tr:first-of-type{
    background-color: darkgray;
  } */
.sr-pl-sh-table thead tr:first-of-type th {
  color: #000;
  font-size: 15px;
  font-weight: initial;
}
.sr-pl-sh-table-pricing .pricing-plan-td-text {
  margin: 20px 0px 5px 0px;
  color: dodgerblue;
  font-weight: 500;
  display: inline-block;
}

.sr-pl-sh-plans-date {
  float: right;
  margin: 20px 0px 5px 0px;
}

.wrap-service-plan-show .inv-total-text,
.inv-total-text {
  font-size: larger;
  font-weight: 600;
}

.wrap-service-plan-show .invoices-type-draft {
  color: #ff5252;
  min-width: 200px;
}

.invoices-type-failed,
.invoices-type-voided {
  color: #ff5252;
}

.invoices-type-overdue {
  color: #ff6f00;
}

.invoices-type-paid {
  color: #4caf50;
}
.invoices-type-partially_paid {
  color: #91a5af;
}
.invoices-type-sent,
.invoices-type-open,
.invoices-type-pending {
  color: #1976d2;
}

.wrap-service-plan-show .invoices-status-text {
  /* border: solid 1px;
    text-align: center;
    min-width: 100px;
    max-width: 100px; */
  display: inline-block;
  text-transform: uppercase;
}
.inv-edit-icon {
  vertical-align: bottom;
}
#inv-status-td {
  min-width: 200px;
}

/* Make DIV sections to look like table rows */
.inv-desc-table-style {
  background-color: white !important;
  /* box-shadow: 0 2px 1px -1px rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 1px 3px 0 rgba(0,0,0,.12)!important; */
}

/*Invoice total section*/
.inv-desc-total {
  text-align: right;
  font-size: 14px;
}

.inv-desc-amount-titles {
  display: inline-block;
  padding-right: 50px;
  font-size: 14px;
}

.inv-desc-amount-values {
  display: inline-block;
  text-align: left;
  width: 208px;
  padding: 24px;
}

.inv-edit-table-desc > .v-table__overflow {
  overflow-x: hidden !important;
  overflow-y: hidden !important;
}

.wrap-inv-description {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}
.memo {
  padding: 15px 24px;
  font-size: 13px;
}
</style>
